// src/pages/MaterialViewer.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PdfHighlighterComponent from "../components/PdfHighlighter";
import Notes from "../components/Notes";
import Layout from "../components/Layout";
import api from "../api/api";

export default function MaterialViewer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [material, setMaterial] = useState(null);
  const [pdfData, setPdfData] = useState(null); // ArrayBuffer from API
  const [pdfUrl, setPdfUrl] = useState(null); // ✅ Blob URL for PdfHighlighter
  const [tab, setTab] = useState("preview");
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const token = localStorage.getItem("token");

  // --- Fetch material metadata ---
  useEffect(() => {
    async function loadMaterial() {
      try {
        const res = await api.get(`/prep/${id}`);
        setMaterial(res.data || res);
      } catch (err) {
        console.error("❌ Failed to fetch material", err);
      } finally {
        setLoading(false);
      }
    }
    loadMaterial();
  }, [id]);

  // --- Fetch PDF as ArrayBuffer ---
  const fetchPdf = useCallback(async () => {
    if (!material) return;
    setPdfLoading(true);
    setPdfError(null);

    try {
      const res = await api.get(`/prep/${material.id}/preview`, {
        responseType: "arraybuffer",
        headers: { Authorization: `Bearer ${token}` },
      });

      const contentType = res.headers["content-type"];
      if (!contentType?.includes("application/pdf")) {
        throw new Error("Received non-PDF response from server");
      }

      setPdfData(res.data);

      // ✅ Convert ArrayBuffer to Blob URL
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

    } catch (err) {
      console.error("❌ Failed to load PDF preview", err);
      setPdfError("Failed to load PDF preview.");
      setPdfData(null);
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  }, [material, token]);

  // --- Load PDF when switching to preview/highlights ---
  useEffect(() => {
    if (material && (tab === "preview" || tab === "highlights") && !pdfData) {
      fetchPdf();
    }
  }, [material, tab, pdfData, fetchPdf]);

  // --- Clean up Blob URL when unmounting / material changes ---
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // --- Download PDF ---
  const handleDownload = async () => {
    try {
      const res = await api.get(`/prep/${material.id}/download`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = material.originalName || "material.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Download failed", err);
      alert("Failed to download material");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 animate-pulse text-gray-500">Loading material...</div>
      </Layout>
    );
  }

  if (!material) {
    return (
      <Layout>
        <div className="p-6 text-red-500">Material not found.</div>
        <button
          onClick={() => navigate("/prep")}
          className="mt-4 px-3 py-1 bg-gray-200 rounded"
        >
          Back to Materials
        </button>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">{material.title}</h1>
            <p className="text-sm text-gray-500">{material.originalName || ""}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate("/prep")
              }
              className="px-3 py-1 border rounded hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Download
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex gap-3">
          {["preview", "notes", "highlights"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1 rounded transition ${
                tab === t ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-4 min-h-[500px]">
          {tab === "preview" && (
            <>
              {pdfLoading && <p className="text-center text-gray-500">Loading preview...</p>}
              {pdfError && <p className="text-center text-red-500">{pdfError}</p>}
              {!pdfLoading && pdfUrl && (
                <PdfHighlighterComponent
                  key={`preview-${material.id}`}
                  materialId={material.id}
                  fileUrl={pdfUrl} // ✅ Blob URL passed here
                  mode="preview"
                />
              )}
            </>
          )}

          {tab === "notes" && <Notes materialId={material.id} />}

          {tab === "highlights" && (
            <>
              {pdfLoading && <p className="text-center text-gray-500">Loading highlights...</p>}
              {pdfError && <p className="text-center text-red-500">{pdfError}</p>}
              {!pdfLoading && pdfUrl && (
                <PdfHighlighterComponent
                  key={`highlights-${material.id}`}
                  materialId={material.id}
                  fileUrl={pdfUrl} // ✅ Blob URL passed here
                  mode="list"
                />
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
